// services/seniority.js — трудов стаж (чл. 355 КТ), клас прослужено време
// (НСОРЗ чл. 12) и отчет за работни дни. Чисти функции, без база и HTTP.
//
// Нормативна основа: КТ чл. 351, 352, 355; Наредба за трудовия стаж
// (загл. изм. ДВ 65/2024) чл. 9; НСОРЗ чл. 12 и чл. 15, ал. 1;
// ПМС № 147/2007 (минимум 0,6%/год.). Стажът се доказва с трудова/
// осигурителна книжка, УП-3, а от 01.06.2025 г. — и с единния електронен
// трудов запис.
//
// чл. 355 КТ е ИЗМЕНЕН (ДВ, бр. 69 от 31.07.2026 г.) В СИЛА ОТ 01.09.2026
// (сверено дословно в lex.bg — по-ранната бележка тук твърдеше 01.01.2027):
// ал. 1 добавя изчисляване „в часове", а ал. 2 заменя стария праг „поне
// половината от работното време = цял ден" с ПРОПОРЦИОНАЛНО зачитане при
// непълно работно време. Затова periodSeniority дели периода на границата:
// до 31.08.2026 вкл. важи старият ред, от 01.09.2026 — пропорцията.

const { round2 } = require('./totals');
const holidays = require('./holidays');

// Законоустановеното ПЪЛНО дневно работно време (чл. 136 КТ).
const LEGAL_DAY_HOURS = 8;

// Първият ден на новия ред по чл. 355 (ред. ДВ, бр. 69 от 2026 г.).
const PROPORTIONAL_FROM = '2026-09-01';

// Минимумът 0,6% за всяка година придобит стаж и професионален опит идва
// от ПМС № 147/29.06.2007 (ДВ 56/2007) — непроменян оттогава. По-висок
// процент е позволен (КТД/вътрешни правила — НСОРЗ чл. 12, ал. 7).
const MIN_RATE_PER_YEAR = 0.6;

// Правото на клас възниква при стаж НЕ ПО-МАЛЪК от 1 година (НСОРЗ чл. 12,
// ал. 9), а размерът се изменя на период не по-малък от 1 година (ал. 10) —
// затова се броят само ПЪЛНИТЕ години.
const MIN_YEARS_FOR_BONUS = 1;

const pad2 = (n) => (n < 10 ? '0' : '') + n;

function parseIso(iso) {
  const m = String(iso || '').match(/^(\d{4})-(\d{2})-(\d{2})$/);
  if (!m) throw new Error('Невалидна дата: ' + iso + ' (очаква се ГГГГ-ММ-ДД).');
  return { y: Number(m[1]), m: Number(m[2]), d: Number(m[3]) };
}

// Колко дни има месецът ПРЕДИ дадения (за заема при изваждане на дати).
function daysInPrevMonth(y, m) {
  const pm = m === 1 ? 12 : m - 1;
  const py = m === 1 ? y - 1 : y;
  return new Date(Date.UTC(py, pm, 0)).getUTCDate();
}

// ---- Раздел А. Трудов стаж (method: 'до-2026') ------------------------------

// Календарната триада (пълни години, месеци, остатък дни) от дата до дата
// ВКЛЮЧИТЕЛНО — класическото броене като в трудова книжка. Само аритметика,
// без правилото за непълното време (то е в periodSeniority).
function calendarTriad(fromIso, toIso) {
  const from = parseIso(fromIso);
  const toIncl = parseIso(toIso);
  // „Включително двете дати" = изключващ край един ден напред.
  const t = new Date(Date.UTC(toIncl.y, toIncl.m - 1, toIncl.d + 1));
  const end = { y: t.getUTCFullYear(), m: t.getUTCMonth() + 1, d: t.getUTCDate() };
  let days = end.d - from.d;
  let months = end.m - from.m;
  let years = end.y - from.y;
  if (days < 0) { days += daysInPrevMonth(end.y, end.m); months--; }
  if (months < 0) { months += 12; years--; }
  return { years, months, days };
}

// Триада ↔ „книжни" дни (конвенцията 30 дни/месец, 12 месеца/година — същата
// като в sumSeniority). Нужно за пропорцията при непълно работно време.
function triadToDays(s) { return s.years * 360 + s.months * 30 + s.days; }
function daysToTriad(total) {
  let d = Math.max(0, Math.round(total));
  const years = Math.floor(d / 360); d -= years * 360;
  const months = Math.floor(d / 30); d -= months * 30;
  return { years, months, days: d };
}
function dayBefore(iso) {
  const p = parseIso(iso);
  const t = new Date(Date.UTC(p.y, p.m - 1, p.d - 1));
  return t.getUTCFullYear() + '-' + pad2(t.getUTCMonth() + 1) + '-' + pad2(t.getUTCDate());
}

// Стаж от дата до дата, ВКЛЮЧИТЕЛНО двете (класическото броене като в
// трудова книжка). Периодът в трудово правоотношение се брои календарно —
// почивните и празничните дни СА стаж (чл. 352, ал. 1, т. 1).
//
// Опции: hoursPerDay — непълно работно време. Правилото ЗАВИСИ ОТ ДАТАТА
// (чл. 355, ал. 2, ред. ДВ, бр. 69 от 2026 г., в сила от 01.09.2026):
//   • до 31.08.2026 (старият ред): ден се брои ЦЯЛ при поне половината от
//     законоустановеното; под това стаж няма и се връща предупреждение;
//   • от 01.09.2026: стажът е ПРОПОРЦИОНАЛЕН на работното време — и под
//     половината вече носи стаж. Период през границата се дели на две.
// excludedDays — дни, които не се зачитат (самоотлъчки, неплатен отпуск
// над зачитания) — изваждат се от остатъка (практическо приближение).
function periodSeniority(opts) {
  const o = opts || {};
  parseIso(o.from); parseIso(o.to);
  if (o.from > o.to) throw new Error('Началото е след края на периода.');

  const h = o.hoursPerDay != null ? Number(o.hoursPerDay) : null;
  if (h != null && !(h > 0)) throw new Error('Невалидни часове на ден: ' + o.hoursPerDay);
  const partTime = h != null && h < LEGAL_DAY_HOURS;

  let result;
  let warning = '';
  let note = '';
  if (!partTime) {
    // Пълно време: пълен стаж и по двата реда.
    result = calendarTriad(o.from, o.to);
  } else {
    const ratio = h / LEGAL_DAY_HOURS;
    // Частите преди/след влизането на новия ред (period може да е изцяло
    // в едната, изцяло в другата или през границата).
    const oldTo = o.to < PROPORTIONAL_FROM ? o.to : dayBefore(PROPORTIONAL_FROM);
    const newFrom = o.from >= PROPORTIONAL_FROM ? o.from : PROPORTIONAL_FROM;
    let totalDays = 0;
    if (o.from <= oldTo) {
      // Старият ред: поне половината → целият период; под половината → нищо.
      if (h >= LEGAL_DAY_HOURS / 2) {
        totalDays += triadToDays(calendarTriad(o.from, oldTo));
      } else {
        warning = 'До 31.08.2026 г. (старият чл. 355, ал. 2): работно време под половината от законоустановеното ('
          + h + ' от ' + LEGAL_DAY_HOURS + ' ч.) не се зачита за трудов стаж — тази част от периода не носи стаж.';
      }
    }
    if (newFrom <= o.to) {
      totalDays += Math.round(triadToDays(calendarTriad(newFrom, o.to)) * ratio);
      note = 'От 01.09.2026 г. непълното работно време се зачита пропорционално ('
        + h + '/' + LEGAL_DAY_HOURS + ') — чл. 355, ал. 2, ред. ДВ, бр. 69 от 2026 г.';
    }
    result = daysToTriad(totalDays);
    // Целият период е бил под половината И преди границата → старото
    // поведение: нула стаж с предупреждение (без бележка за новия ред).
    if (!note && warning) return { years: 0, months: 0, days: 0, warning };
  }

  if (o.excludedDays) {
    let { years, months, days } = result;
    days -= Number(o.excludedDays) || 0;
    while (days < 0) { days += 30; months--; }
    while (months < 0) { months += 12; years--; }
    if (years < 0) { years = 0; months = 0; days = 0; }
    result = { years, months, days };
  }

  if (warning) result.warning = warning;
  if (note) result.note = note;
  return result;
}

// Сумиране на периоди по правилото от трудовите книжки: дните се събират и
// daysPerMonth дни стават 1 месец; 12 месеца — 1 година.
//
// ВАЖНО: „30 дни = 1 месец" е СЧЕТОВОДНА КОНВЕНЦИЯ от практиката по
// трудовите книжки, НЕ нормативен текст. Нормативната алтернатива е
// делението на 21 (Наредба за трудовия стаж, чл. 9, ал. 4), което важи за
// изработени РАБОТНИ дни в непълни месеци при подневно отчитане. Затова:
// daysPerMonth: 30 (подразбиране — сумиране на календарни периоди от
// документи) или 21 (броене на изработени работни дни).
function sumSeniority(list, opts) {
  const per = Number((opts || {}).daysPerMonth) || 30;
  if (per !== 30 && per !== 21) throw new Error('daysPerMonth може да е 30 или 21.');
  let years = 0, months = 0, days = 0;
  for (const s of (list || [])) {
    years += Number(s && s.years) || 0;
    months += Number(s && s.months) || 0;
    days += Number(s && s.days) || 0;
  }
  months += Math.floor(days / per);
  days = days % per;
  years += Math.floor(months / 12);
  months = months % 12;
  return { years, months, days };
}

// ---- Раздел Б. Клас прослужено време (НСОРЗ чл. 12) -------------------------

// Процент върху ОСНОВНАТА работна заплата по индивидуалния трудов договор
// (ал. 1 — не върху брутната!). Броят се само пълните години (ал. 10);
// под 1 година клас няма (ал. 9). Ставката е поне MIN_RATE_PER_YEAR.
function experienceBonus(opts) {
  const o = opts || {};
  const base = Number(o.baseSalary);
  if (!(base >= 0)) throw new Error('Въведете основна заплата.');
  const s = o.seniority || o.seniorityYearsMonthsDays || {};
  const rate = o.ratePerYear == null ? MIN_RATE_PER_YEAR : Number(o.ratePerYear);
  if (!(rate >= MIN_RATE_PER_YEAR)) {
    throw new Error('Ставката (' + rate + '%) е под минимума 0,6% за година — ПМС № 147/2007.');
  }

  const fullYears = Number(s.years) || 0;
  const notes = [
    'Класът се изчислява върху ОСНОВНАТА заплата по договора, не върху брутната (НСОРЗ чл. 12, ал. 1).',
    'Признава се стаж на същата, сходна или със същия характер работа/длъжност — по преценка на работодателя (ал. 2–5).',
    'Размерът се изменя при всяка навършена ПЪЛНА година (ал. 10).',
    'Плаща се за действително отработено време, по основното правоотношение (ал. 8).',
    'Класът е добавка с постоянен характер (НСОРЗ чл. 15, ал. 1) — влиза в базата за обезщетения.',
  ];

  if (fullYears < MIN_YEARS_FOR_BONUS) {
    notes.unshift('Стажът е под 1 година — правото на клас още не е възникнало (НСОРЗ чл. 12, ал. 9).');
    return { fullYears, percent: 0, monthlyAmount: 0, notes };
  }

  const percent = round2(fullYears * rate);
  const monthlyAmount = round2(base * percent / 100);
  return { fullYears, percent, monthlyAmount, notes };
}

// ---- Раздел В. Работни дни между дати (изцяло върху holidays.js) ------------

// Отчет за период (вкл. двете дати): календарни, работни, почивни дни и
// списъкът официални празници в периода (вкл. пренесените по чл. 154,
// ал. 2 с обяснение). Разместените от МС дни важат само ако са подадени
// като overrides — панелът в калкулатора работи с ОБЩИЯ календар.
function workdaysReport(opts) {
  const o = opts || {};
  parseIso(o.from); parseIso(o.to);
  if (o.from > o.to) throw new Error('Началото е след края на периода.');
  const from = new Date(o.from + 'T00:00:00Z');
  const to = new Date(o.to + 'T00:00:00Z');
  const calendarDays = Math.round((to - from) / 86400000) + 1;
  const workingDays = holidays.workingDaysBetween(o.from, o.to, o.overrides);
  // ВСИЧКИ години от периода (преглед 17.09.2026): само първата и
  // последната пропускаха празниците на годините по средата — тригодишна
  // справка излизаше без нито един празник на средната година.
  const years = [];
  for (let y = Number(o.from.slice(0, 4)); y <= Number(o.to.slice(0, 4)); y++) years.push(String(y));
  const list = [];
  for (const y of years) {
    for (const h of holidays.holidaysForYear(y, o.overrides)) {
      if (h.date >= o.from && h.date <= o.to) list.push(h);
    }
  }
  list.sort((a, b) => (a.date < b.date ? -1 : 1));
  return { calendarDays, workingDays, nonWorkingDays: calendarDays - workingDays, holidays: list };
}

module.exports = {
  periodSeniority,
  sumSeniority,
  experienceBonus,
  workdaysReport,
  LEGAL_DAY_HOURS,
  MIN_RATE_PER_YEAR,
  MIN_YEARS_FOR_BONUS,
};
